import { Modal } from "@mui/material";
import { Close } from "@mui/icons-material";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import Styles from "./RASelectionModal.module.css";
import { formatDateType } from "../../../commons/FormatDate";
import CircularProgress from "../../ReverseAuction/Bidding/CircularProgress";
import { usePdfPreview } from "../../../hooks/usePdfPreview";
import ConfirmationModal from "../../../commons/ConfirmationModal/ConfirmationModal";
import { useRASelectionModal } from "./useRASelectionModal";
import BidRow from "./BidRow";

const RASelectionModal = ({ open, handleClose, tenderData }) => {
  const { PdfPreviewModal } = usePdfPreview();

  const {
    scrollAreaRef,
    tabRefs,
    expanded,
    reqList,
    activeReq,
    editingAlloc,
    setEditingAlloc,
    selectedBids,
    rejectedBids,
    sessionAbscondconnectIds,
    confirmation,
    setConfirmation,
    rejectConfirmation,
    setRejectConfirmation,
    updateQtyConfirmation,
    setUpdateQtyConfirmation,
    isCloseDatePassed,
    showSellerMinQty,
    hasAnyAccepted,
    showNoBidState,
    allBidsRejected,
    getFilledQty,
    onToggle,
    handleSelectBid,
    handleRejectBid,
    handleClear,
    openEditAlloc,
    closeEditAlloc,
    handleUpdateQuantityRequest,
    handleModalSubmit,
    handleFinalizeT_C,
    handleRejectAndClose,
    resetUpdateQtyConfirmation,
  } = useRASelectionModal(tenderData, handleClose);

  const sortedBids = [...(activeReq?.bids ?? [])].sort(
    (a, b) => (a?.bidPrice ?? 0) - (b?.bidPrice ?? 0),
  );
  const isFirstBidWithdrawn = sortedBids[0]?.winnerBidStatus === "WITHDRAWN";
  const isFirstBidSelected =
    sortedBids.length > 0 && selectedBids.includes(sortedBids[0]?.id);

  return (
    <>
      {/* req 4: popup note explicitly mentions cascade for non-first bids */}
      {updateQtyConfirmation?.status && (
        <ConfirmationModal
          open={updateQtyConfirmation.status}
          handleClose={resetUpdateQtyConfirmation}
          header="Confirm Quantity Update"
          data="Are you sure you want to update the allocated quantity?"
          note={
            showSellerMinQty &&
            updateQtyConfirmation.minBidQuantityMw &&
            Number(updateQtyConfirmation.editingQty) <
              Number(updateQtyConfirmation.minBidQuantityMw)
              ? `Updating quantity below the seller minimum quantity (${updateQtyConfirmation.minBidQuantityMw} MW) will mark this bid as REJECTED.`
              : ""
          }
          handleSubmit={() => {
            handleUpdateQuantityRequest(
              updateQtyConfirmation.connectId,
              updateQtyConfirmation.minQty,
              updateQtyConfirmation.index,
              updateQtyConfirmation.minBidQuantityMw,
            );
            resetUpdateQtyConfirmation();
          }}
        />
      )}

      {rejectConfirmation?.status && (
        <ConfirmationModal
          open={rejectConfirmation?.status}
          handleClose={() =>
            setRejectConfirmation({ status: false, header: "", data: "" })
          }
          data={rejectConfirmation?.data}
          header={rejectConfirmation?.header}
          handleSubmit={handleRejectAndClose}
          type="TenderBid"
        />
      )}

      {confirmation?.status && (
        <ConfirmationModal
          open={confirmation?.status}
          handleClose={() =>
            setConfirmation({ status: false, header: "", data: "", note: "" })
          }
          data={confirmation?.data}
          header={confirmation?.header}
          note={confirmation?.note}
          handleSubmit={handleFinalizeT_C}
          type="TenderBid"
        />
      )}

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
      >
        <div className={`popupModal ${Styles.viewSelectionContainer}`}>
          <PdfPreviewModal />

          {/* Header */}
          <div
            className={`display_flex_row modalHeading ${Styles.modalHeading}`}
          >
            <div className="headingText">View & Finalize Bid(s)</div>
            <Close
              style={{ fontSize: "24px", color: "#777777", cursor: "pointer" }}
              onClick={handleClose}
            />
          </div>

          {/* Requisition tabs — req 6: no pending count badge */}
          <div className={Styles.reqSelectionListContainer}>
            <div
              onClick={() => expanded !== 0 && onToggle(expanded - 1)}
              className={`${Styles.firstReqIndicator} ${expanded !== 0 ? Styles.activeStateButton : ""}`}
            >
              <KeyboardDoubleArrowLeftIcon style={{ fontSize: "14px" }} />
            </div>
            <div className={Styles.reqSelectionScrollArea} ref={scrollAreaRef}>
              {reqList?.map((ele, index) => (
                <div
                  key={ele?.id}
                  className={`${Styles.reqSelectionText} ${expanded === index ? Styles.activeStateButton : ""}`}
                  ref={(el) => (tabRefs.current[index] = el)}
                  onClick={() => onToggle(index)}
                >
                  <span>Requisition {index + 1}</span>
                </div>
              ))}
            </div>
            <div
              onClick={() =>
                expanded !== reqList?.length - 1 && onToggle(expanded + 1)
              }
              className={`${Styles.lastReqIndicator} ${expanded !== reqList?.length - 1 ? Styles.activeStateButton : ""}`}
            >
              <KeyboardDoubleArrowRightIcon style={{ fontSize: "14px" }} />
            </div>
          </div>

          {/* Requisition summary */}
          <div className={Styles.reqSelfDetailsContainer}>
            <div className={Styles.reqDetailsRow}>
              <div>Date Range</div>
              <div>
                {formatDateType(activeReq?.dateStartAt)} -{" "}
                {formatDateType(activeReq?.dateEndAt)}
              </div>
            </div>
            <div className={Styles.reqDetailsRow}>
              <div>Time Range</div>
              <div>
                {activeReq?.slotStartAt} to{" "}
                {activeReq?.slotEndAt === "23:59"
                  ? "24:00"
                  : activeReq?.slotEndAt}
              </div>
            </div>
            <div className={Styles.reqDetailsRow}>
              <div>Required Qty</div>
              <div>{activeReq?.requiredQty} MW</div>
            </div>
            <div className={Styles.reqDetailsRow}>
              <div>Minimum Qty</div>
              <div>{activeReq?.minimumQty} MW</div>
            </div>
            <div className={Styles.reqDetailsRow}>
              <div>Lowest Bid Price</div>
              <div>₹{activeReq?.lowestBidPrice}/kWh</div>
            </div>
            <div className={Styles.reqDetailsRow}>
              <div>Bucket Filled</div>
              <div style={{ textAlign: "center" }}>
                <CircularProgress
                  value={getFilledQty(activeReq)}
                  size={40}
                  strokeWidth={4}
                  required={activeReq?.requiredQty}
                />
              </div>
            </div>
          </div>

          {/* Bid table */}
          <div className={Styles.reqDetailsRowHeading}>
            Requisition Bid List
          </div>

          <div className={Styles.bidDetailsContainer}>
            <div
              className={`display_flex_row tableHeading ${Styles.bidDetailsTable}`}
            >
              <div style={{ width: "15%" }}>Bid Price (₹/kWh)</div>
              <div style={{ width: "15%" }}>Bid Qty (MW)</div>
              {showSellerMinQty && (
                <div style={{ width: "15%" }}>Seller Min. Quantity (MW)</div>
              )}
              <div style={{ width: "25%" }}>Allocated Qty (MW)</div>
              <div style={{ width: "15%" }}>Bank Guarantee</div>
              <div
                style={{
                  width: "20%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <span>Status</span>
                {/* req 5: clear selection always available when any bid is acted on */}
                {(selectedBids.length > 0 || rejectedBids.length > 0) && (
                  <span
                    onClick={handleClear}
                    className={`activeButton_type_2 ${Styles.clearSelectionBtn}`}
                  >
                    Clear Selection
                  </span>
                )}
              </div>
            </div>

            <div className={`scroll_bar_y ${Styles.bidDetailsScrollArea}`}>
              {sortedBids.length === 0 ? (
                <div
                  className="display_flex_row"
                  style={{
                    justifyContent: "center",
                    padding: "16px 0",
                    color: "#888",
                  }}
                >
                  No bids
                </div>
              ) : (
                sortedBids.map((bid, index) => (
                  <BidRow
                    key={bid?.id}
                    bid={bid}
                    index={index}
                    isFirstBidWithdrawn={isFirstBidWithdrawn}
                    isFirstBidSelected={isFirstBidSelected}
                    isCloseDatePassed={isCloseDatePassed}
                    showSellerMinQty={showSellerMinQty}
                    activeReq={activeReq}
                    selectedBids={selectedBids}
                    rejectedBids={rejectedBids}
                    sessionAbscondconnectIds={sessionAbscondconnectIds}
                    editingAlloc={editingAlloc}
                    setEditingAlloc={setEditingAlloc}
                    setUpdateQtyConfirmation={setUpdateQtyConfirmation}
                    openEditAlloc={openEditAlloc}
                    closeEditAlloc={closeEditAlloc}
                    onSelect={handleSelectBid}
                    onReject={handleRejectBid}
                  />
                ))
              )}
            </div>
          </div>

          {/* Footer */}
          {showNoBidState ? (
            <>
              <div
                className={`${Styles.bidDetailsContainer} ${Styles.tenderHeader}`}
              >
                Note: Bidding period has ended with no bids placed. You cannot
                proceed further.
              </div>
              <div className={Styles.buttonMainContainer}>
                <div className="activeButton" onClick={handleClose}>
                  Close
                </div>
              </div>
            </>
          ) : (
            <>
              {isCloseDatePassed && (
                <div
                  className={`${Styles.bidDetailsContainer} ${Styles.tenderHeader}`}
                >
                  Note: The tender has ended, you cannot proceed further.
                </div>
              )}
              <div className={Styles.buttonMainContainer}>
                <div className="activeButton_type_3" onClick={handleClose}>
                  Cancel
                </div>
                {allBidsRejected ? (
                  <div
                    onClick={() =>
                      !isCloseDatePassed &&
                      setRejectConfirmation({
                        status: true,
                        header: "Confirm Reject & Close",
                        data: "Are you sure you want to reject all bids and close the tender? This action cannot be undone.",
                      })
                    }
                    className={
                      isCloseDatePassed
                        ? "activeButton_disable"
                        : "activeButton"
                    }
                  >
                    Reject All and Close Tender
                  </div>
                ) : expanded !== reqList?.length - 1 ? (
                  <div
                    className={
                      isCloseDatePassed
                        ? "activeButton_disable"
                        : "activeButton"
                    }
                    onClick={() => !isCloseDatePassed && onToggle(expanded + 1)}
                  >
                    Save and Continue
                  </div>
                ) : hasAnyAccepted ? (
                  // req 3: at least one bid accepted → proceed
                  <div
                    onClick={() => !isCloseDatePassed && handleModalSubmit()}
                    className={
                      isCloseDatePassed
                        ? "activeButton_disable"
                        : "activeButton"
                    }
                  >
                    Proceed to Documentation
                  </div>
                ) : (
                  // req 3: no bids selected → show Close instead of disabled Proceed
                  <div className="activeButton" onClick={handleClose}>
                    Close
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

export default RASelectionModal;
